import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import {
    Users, Search, Filter, Archive, ArchiveRestore, GraduationCap, ChevronRight,
    ArrowUpRight, Download, RefreshCw, UserCheck, UsersRound, AlertTriangle,
    CheckSquare, Square, Bell, Mail,
} from "lucide-react";

const FILIERE_LABELS = {
    electrotechnique: "BTS Électrotechnique",
    ciel: "BTS CIEL",
};

const NIVEAU_LABELS = { "1ere": "1ère année", "2eme": "2ème année" };
const STATUS_LABELS = {
    active: "Active", archived: "Archivée", graduated: "Diplômée", any: "Toutes",
};

const STORAGE_KEY = "profawz-students-filters";

function computeCurrentSchoolYear() {
    const now = new Date();
    const y = now.getFullYear();
    return now.getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function buildSchoolYearOptions() {
    const cy = computeCurrentSchoolYear();
    const [y1] = cy.split("-").map(Number);
    const list = [];
    for (let i = 2; i >= -1; i--) {
        list.push(`${y1 - i}-${y1 - i + 1}`);
    }
    return list;
}

function loadFilters() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    } catch (e) {
        console.warn("[Students] loadFilters failed", e);
    }
    return {};
}

function saveFilters(f) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(f)); } catch (e) {
        console.warn("[Students] saveFilters failed", e);
    }
}

export default function Students() {
    const { user } = useAuth();
    const initial = loadFilters();
    const [schoolYear, setSchoolYear] = useState(initial.schoolYear || computeCurrentSchoolYear());
    const [filiere, setFiliere] = useState(initial.filiere || "all");
    const [niveau, setNiveau] = useState(initial.niveau || "all");
    const [promotionId, setPromotionId] = useState(initial.promotionId || "all");
    const [status, setStatus] = useState(initial.status || "active");
    const [q, setQ] = useState("");
    const [students, setStudents] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [stats, setStats] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [busyAction, setBusyAction] = useState(false);

    const isResponsable = user?.role === "responsable" || user?.role === "admin";
    const isFormateur = user?.role === "formateur" || isResponsable;

    // Persist filters
    useEffect(() => {
        saveFilters({ schoolYear, filiere, niveau, promotionId, status });
    }, [schoolYear, filiere, niveau, promotionId, status]);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get(`/dashboard/school-stats?school_year=${schoolYear}`);
            setStats(data);
        } catch (e) {
            console.warn("[Students] loadStats failed", e);
        }
    }, [schoolYear]);

    const loadPromotions = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (schoolYear) params.set("school_year", schoolYear);
            if (filiere && filiere !== "all") params.set("filiere", filiere);
            const { data } = await api.get(`/promotions?${params.toString()}`);
            setPromotions(Array.isArray(data) ? data : []);
        } catch (e) {
            console.warn("[Students] loadPromotions failed", e);
            setPromotions([]);
        }
    }, [schoolYear, filiere]);

    const loadStudents = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (schoolYear) params.set("school_year", schoolYear);
            if (filiere && filiere !== "all") params.set("filiere", filiere);
            if (niveau && niveau !== "all") params.set("niveau", niveau);
            if (promotionId && promotionId !== "all") params.set("promotion_id", promotionId);
            if (status) params.set("status", status);
            if (q && q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/students?${params.toString()}`);
            setStudents(Array.isArray(data) ? data : []);
            // Clear selection on filter change
            setSelected(new Set());
        } catch (e) {
            console.warn("[Students] loadStudents failed", e);
            toast.error("Impossible de charger la liste des étudiants");
            setStudents([]);
        } finally {
            setLoading(false);
        }
    }, [schoolYear, filiere, niveau, promotionId, status, q]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadPromotions(); }, [loadPromotions]);

    // Debounce search input
    useEffect(() => {
        const t = setTimeout(() => { loadStudents(); }, 250);
        return () => clearTimeout(t);
    }, [loadStudents]);

    const filteredPromotions = useMemo(() => {
        if (!Array.isArray(promotions)) return [];
        return promotions.filter((p) => (filiere === "all" || p.filiere === filiere));
    }, [promotions, filiere]);

    const resetFilters = () => {
        setSchoolYear(computeCurrentSchoolYear());
        setFiliere("all");
        setNiveau("all");
        setPromotionId("all");
        setStatus("active");
        setQ("");
    };

    const toggleOne = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        setSelected((prev) => {
            if (prev.size === students.length) return new Set();
            return new Set(students.map((s) => s.id));
        });
    };

    const bulkArchive = async (archive) => {
        if (!selected.size) return;
        const label = archive ? "archiver" : "restaurer";
        if (!window.confirm(`Voulez-vous ${label} ${selected.size} étudiant(s) ?`)) return;
        setBusyAction(true);
        try {
            await api.post("/students/bulk-archive", { student_ids: Array.from(selected), archive });
            toast.success(`${selected.size} étudiant(s) ${archive ? "archivé(s)" : "restauré(s)"}`);
            setSelected(new Set());
            await Promise.all([loadStudents(), loadStats()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action impossible");
        } finally {
            setBusyAction(false);
        }
    };

    const quickUnarchive = async (sid, prenom, nom) => {
        if (!window.confirm(`Restaurer ${prenom || ""} ${nom || ""} ?`)) return;
        try {
            await api.post(`/students/${sid}/unarchive`);
            toast.success("Étudiant restauré");
            await Promise.all([loadStudents(), loadStats()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restauration impossible");
        }
    };

    const singleAlternantReminder = async (sid, email) => {
        if (!window.confirm(`Envoyer un rappel « créer un bilan » à ${email} ?`)) return;
        try {
            const { data } = await api.post(`/students/${sid}/alternant-reminder`);
            toast.success(`Rappel envoyé à ${data.sent_to}`);
            await loadStudents();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Envoi impossible");
        }
    };

    const bulkAlternantReminder = async () => {
        if (!selected.size) return;
        if (!window.confirm(`Envoyer un rappel « créer un bilan » à ${selected.size} étudiant(s) ?`)) return;
        setBusyAction(true);
        try {
            const { data } = await api.post("/students/bulk-alternant-reminder", { student_ids: Array.from(selected) });
            toast.success(`${data.sent} rappel(s) envoyé(s)${data.skipped ? ` · ${data.skipped} ignoré(s)` : ""}`);
            setSelected(new Set());
            await loadStudents();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Envoi impossible");
        } finally {
            setBusyAction(false);
        }
    };

    const bulkTutorReminder = async () => {
        if (!selected.size) return;
        if (!window.confirm(`Rappeler les tuteurs des bilans en attente de ${selected.size} étudiant(s) ?`)) return;
        setBusyAction(true);
        try {
            const { data } = await api.post("/students/bulk-tutor-reminder", { student_ids: Array.from(selected) });
            if (data.sent) {
                toast.success(`${data.sent} rappel(s) tuteur envoyé(s)${data.no_pending ? ` · ${data.no_pending} étudiant(s) sans bilan en attente` : ""}`);
            } else {
                toast.info(`Aucun bilan en attente — ${data.no_pending} étudiant(s) sans rappel à envoyer.`);
            }
            setSelected(new Set());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Envoi impossible");
        } finally {
            setBusyAction(false);
        }
    };

    const exportSelected = async () => {
        if (!selected.size) return;
        toast.info(`Export de ${selected.size} étudiant(s) — bientôt disponible (Phase D)`);
    };

    const allSelected = students.length > 0 && selected.size === students.length;
    const someSelected = selected.size > 0 && !allSelected;

    return (
        <div className="min-h-screen flex flex-col bg-cream">
            <Navbar />
            <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-ink flex items-center gap-2" data-testid="students-title">
                            <UsersRound className="w-6 h-6 sm:w-7 sm:h-7 text-primary" /> Étudiants
                        </h1>
                        <p className="text-sm text-ink/70 mt-1">
                            Vue par année scolaire, formation, niveau et promotion. Un profil unique par étudiant, pour toute sa scolarité.
                        </p>
                    </div>
                    {isResponsable && (
                        <Link
                            to="/promotions"
                            className="btn-outline text-sm inline-flex items-center gap-1.5"
                            data-testid="students-goto-promotions"
                        >
                            <GraduationCap className="w-4 h-4" /> Gérer les promotions <ChevronRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>

                {/* Quick stats */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3" data-testid="students-stats">
                        <StatPill label="1ère année" value={stats.annee_1} />
                        <StatPill label="2ème année" value={stats.annee_2} />
                        <StatPill label="Total actifs" value={stats.total_active} accent />
                        <StatPill label="Diplômés" value={stats.graduated} />
                        <StatPill label="Archivés" value={stats.archived} />
                    </div>
                )}

                {/* Filters bar */}
                <section className="rounded-2xl bg-white border border-ink/10 p-4 sm:p-5 shadow-sm" data-testid="students-filters">
                    <div className="flex items-center gap-2 mb-3 text-ink/70 text-sm font-medium">
                        <Filter className="w-4 h-4" /> Filtres
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <FilterSelect
                            testid="filter-school-year"
                            label="Année scolaire"
                            value={schoolYear}
                            onChange={setSchoolYear}
                            options={buildSchoolYearOptions().map((y) => ({ value: y, label: y }))}
                        />
                        <FilterSelect
                            testid="filter-filiere"
                            label="Formation"
                            value={filiere}
                            onChange={(v) => { setFiliere(v); setPromotionId("all"); }}
                            options={[
                                { value: "all", label: "Toutes" },
                                ...Object.entries(FILIERE_LABELS).map(([v, l]) => ({ value: v, label: l })),
                            ]}
                        />
                        <FilterSelect
                            testid="filter-niveau"
                            label="Niveau"
                            value={niveau}
                            onChange={setNiveau}
                            options={[
                                { value: "all", label: "Tous" },
                                { value: "1ere", label: "1ère année" },
                                { value: "2eme", label: "2ème année" },
                            ]}
                        />
                        <FilterSelect
                            testid="filter-promotion"
                            label="Promotion"
                            value={promotionId}
                            onChange={setPromotionId}
                            options={[
                                { value: "all", label: "Toutes" },
                                ...filteredPromotions.map((p) => ({ value: p.id, label: p.name })),
                            ]}
                        />
                        <FilterSelect
                            testid="filter-status"
                            label="Statut"
                            value={status}
                            onChange={setStatus}
                            options={Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                        />
                        <div>
                            <label className="block text-xs font-medium text-ink/70 mb-1">Recherche</label>
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-ink/40" />
                                <input
                                    type="text"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Nom, prénom, email…"
                                    className="input pl-8 text-sm w-full"
                                    data-testid="filter-search"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                        <div className="text-xs text-ink/60">
                            {loading ? "Chargement…" : `${students.length} étudiant(s) affiché(s)`}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={loadStudents}
                                className="btn-outline text-xs inline-flex items-center gap-1.5"
                                data-testid="filter-refresh"
                            >
                                <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
                            </button>
                            <button
                                onClick={resetFilters}
                                className="btn-ghost text-xs"
                                data-testid="filter-reset"
                            >
                                Réinitialiser
                            </button>
                        </div>
                    </div>
                </section>

                {/* Bulk actions toolbar */}
                {selected.size > 0 && (
                    <section
                        className="sticky top-2 z-10 rounded-2xl bg-primary/10 border border-primary/30 px-4 py-3 shadow-md flex flex-wrap items-center gap-3"
                        data-testid="students-bulk-toolbar"
                    >
                        <div className="text-sm font-medium text-primary">
                            {selected.size} étudiant(s) sélectionné(s)
                        </div>
                        <div className="flex flex-wrap gap-2 ml-auto">
                            {isFormateur && status !== "archived" && (
                                <>
                                    <button
                                        onClick={bulkAlternantReminder}
                                        disabled={busyAction}
                                        className="btn-outline text-xs inline-flex items-center gap-1.5"
                                        data-testid="bulk-alternant-reminder-btn"
                                        title="Envoyer un rappel aux alternants sélectionnés"
                                    >
                                        <Bell className="w-4 h-4" /> Rappel bilan
                                    </button>
                                    <button
                                        onClick={bulkTutorReminder}
                                        disabled={busyAction}
                                        className="btn-outline text-xs inline-flex items-center gap-1.5"
                                        data-testid="bulk-tutor-reminder-btn"
                                        title="Rappeler les tuteurs pour tous les bilans en attente"
                                    >
                                        <Mail className="w-4 h-4" /> Rappeler les tuteurs
                                    </button>
                                </>
                            )}
                            {isResponsable && status !== "archived" && (
                                <button
                                    onClick={() => bulkArchive(true)}
                                    disabled={busyAction}
                                    className="btn-outline text-xs inline-flex items-center gap-1.5"
                                    data-testid="bulk-archive-btn"
                                >
                                    <Archive className="w-4 h-4" /> Archiver
                                </button>
                            )}
                            {isResponsable && status === "archived" && (
                                <button
                                    onClick={() => bulkArchive(false)}
                                    disabled={busyAction}
                                    className="btn-outline text-xs inline-flex items-center gap-1.5"
                                    data-testid="bulk-restore-btn"
                                >
                                    <ArchiveRestore className="w-4 h-4" /> Restaurer
                                </button>
                            )}
                            <button
                                onClick={exportSelected}
                                disabled={busyAction}
                                className="btn-outline text-xs inline-flex items-center gap-1.5"
                                data-testid="bulk-export-btn"
                            >
                                <Download className="w-4 h-4" /> Exporter
                            </button>
                            <button
                                onClick={() => setSelected(new Set())}
                                className="btn-ghost text-xs"
                                data-testid="bulk-clear-btn"
                            >
                                Tout désélectionner
                            </button>
                        </div>
                    </section>
                )}

                {/* Students list */}
                <section className="rounded-2xl bg-white border border-ink/10 shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-ink/60">Chargement des étudiants…</div>
                    ) : students.length === 0 ? (
                        <div className="p-8 text-center">
                            <Users className="w-10 h-10 text-ink/30 mx-auto mb-3" />
                            <p className="text-ink/70">Aucun étudiant ne correspond à ces filtres.</p>
                            <button onClick={resetFilters} className="btn-outline text-xs mt-3">
                                Réinitialiser les filtres
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-cream/70 text-ink/70 border-b border-ink/10 text-xs uppercase tracking-wide">
                                    <tr>
                                        <th className="p-3 text-left w-10">
                                            <button
                                                onClick={toggleAll}
                                                className="inline-flex items-center"
                                                data-testid="students-select-all"
                                                aria-label="Tout sélectionner"
                                            >
                                                {allSelected ? (
                                                    <CheckSquare className="w-5 h-5 text-primary" />
                                                ) : someSelected ? (
                                                    <CheckSquare className="w-5 h-5 text-primary/60" />
                                                ) : (
                                                    <Square className="w-5 h-5 text-ink/40" />
                                                )}
                                            </button>
                                        </th>
                                        <th className="p-3 text-left">Étudiant</th>
                                        <th className="p-3 text-left">Formation</th>
                                        <th className="p-3 text-left">Niveau</th>
                                        <th className="p-3 text-left">Promotion</th>
                                        <th className="p-3 text-left">Année</th>
                                        <th className="p-3 text-left">Statut</th>
                                        <th className="p-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-ink/10">
                                    {students.map((s) => (
                                        <tr
                                            key={s.id}
                                            className={selected.has(s.id) ? "bg-primary/5" : "hover:bg-cream/40"}
                                            data-testid={`student-row-${s.id}`}
                                        >
                                            <td className="p-3">
                                                <button
                                                    onClick={() => toggleOne(s.id)}
                                                    data-testid={`student-select-${s.id}`}
                                                    aria-label={`Sélectionner ${s.prenom} ${s.nom}`}
                                                >
                                                    {selected.has(s.id) ? (
                                                        <CheckSquare className="w-5 h-5 text-primary" />
                                                    ) : (
                                                        <Square className="w-5 h-5 text-ink/40" />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="p-3">
                                                <div className="font-medium text-ink">
                                                    {(s.prenom || "") + " " + (s.nom || "")} {s.is_archived && (
                                                        <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-ink/10 text-ink/60">archivé</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-ink/60">{s.email}</div>
                                                {s.last_bilan_reminder_sent_at && (
                                                    <div className="text-[11px] text-orange-700 inline-flex items-center gap-1 mt-0.5" data-testid={`student-reminder-badge-${s.id}`}>
                                                        <Bell className="w-3 h-3" /> Rappel envoyé le {new Date(s.last_bilan_reminder_sent_at).toLocaleDateString("fr-FR")}
                                                        {s.bilan_reminders_count > 1 && ` (×${s.bilan_reminders_count})`}
                                                    </div>
                                                )}
                                                {s.pending_tutor_bilans > 0 && (
                                                    <div className="text-[11px] text-amber-700 inline-flex items-center gap-1 mt-0.5">
                                                        <Mail className="w-3 h-3" /> {s.pending_tutor_bilans} bilan(s) en attente d'évaluation tuteur
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3 text-ink/80">
                                                {FILIERE_LABELS[s.enrollment?.filiere] || s.enrollment?.filiere || "—"}
                                            </td>
                                            <td className="p-3">
                                                <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-medium">
                                                    {NIVEAU_LABELS[s.enrollment?.niveau] || "—"}
                                                </span>
                                            </td>
                                            <td className="p-3 text-ink/80 max-w-[220px] truncate" title={s.promotion_name || ""}>
                                                {s.promotion_name || "—"}
                                            </td>
                                            <td className="p-3 text-ink/70">{s.enrollment?.school_year || "—"}</td>
                                            <td className="p-3">
                                                <EnrollmentBadge status={s.enrollment?.status} />
                                            </td>
                                            <td className="p-3 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    {isFormateur && !s.is_archived && s.email && (
                                                        <button
                                                            onClick={() => singleAlternantReminder(s.id, s.email)}
                                                            className="btn-ghost text-xs inline-flex items-center gap-1 text-orange-700"
                                                            data-testid={`student-reminder-${s.id}`}
                                                            title="Envoyer un rappel « créer un bilan »"
                                                        >
                                                            <Bell className="w-4 h-4" /> Rappel
                                                        </button>
                                                    )}
                                                    {isResponsable && s.is_archived && (
                                                        <button
                                                            onClick={() => quickUnarchive(s.id, s.prenom, s.nom)}
                                                            className="btn-ghost text-xs inline-flex items-center gap-1 text-primary"
                                                            data-testid={`student-unarchive-${s.id}`}
                                                            title="Restaurer"
                                                        >
                                                            <ArchiveRestore className="w-4 h-4" /> Restaurer
                                                        </button>
                                                    )}
                                                    {isFormateur && (
                                                        <Link
                                                            to={`/progression/${s.id}`}
                                                            className="btn-ghost text-xs inline-flex items-center gap-1"
                                                            data-testid={`student-progression-${s.id}`}
                                                            title="Progression"
                                                        >
                                                            <ArrowUpRight className="w-4 h-4" /> Suivi
                                                        </Link>
                                                    )}
                                                    <Link
                                                        to={`/alternants/${s.id}/bilan-global`}
                                                        className="btn-ghost text-xs inline-flex items-center gap-1"
                                                        data-testid={`student-bilan-global-${s.id}`}
                                                        title="Bilan global"
                                                    >
                                                        <GraduationCap className="w-4 h-4" /> Bilan
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* Notice pour les alternants */}
                {!isFormateur && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5" />
                        Cette page est réservée aux responsables et formateurs de votre établissement.
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}

function StatPill({ label, value, accent = false }) {
    return (
        <div
            className={`rounded-xl border px-3 py-2 ${accent ? "bg-primary/10 border-primary/30" : "bg-white border-ink/10"}`}
        >
            <div className="text-[10px] uppercase tracking-wide text-ink/60">{label}</div>
            <div className={`text-xl font-bold ${accent ? "text-primary" : "text-ink"}`}>{value}</div>
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-xs font-medium text-ink/70 mb-1">{label}</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="input text-sm w-full"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </div>
    );
}

const STATUS_COLORS = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    promoted: "bg-blue-50 text-blue-700 border-blue-200",
    graduated: "bg-purple-50 text-purple-700 border-purple-200",
    non_graduated: "bg-rose-50 text-rose-700 border-rose-200",
    reoriented: "bg-amber-50 text-amber-700 border-amber-200",
    abandoned: "bg-slate-100 text-slate-700 border-slate-200",
    other: "bg-slate-100 text-slate-700 border-slate-200",
    archived: "bg-ink/10 text-ink/60 border-ink/20",
};
const STATUS_TEXT = {
    active: "Active",
    promoted: "Passé année sup.",
    graduated: "Diplômé",
    non_graduated: "Non diplômé",
    reoriented: "Réorientation",
    abandoned: "Abandon",
    other: "Autre",
    archived: "Archivé",
};

function EnrollmentBadge({ status }) {
    const cls = STATUS_COLORS[status] || "bg-ink/5 text-ink/60 border-ink/10";
    return (
        <span className={`px-2 py-0.5 rounded-full text-xs border ${cls}`}>
            {STATUS_TEXT[status] || status || "—"}
        </span>
    );
}
